/**
 * Jobs System Types
 * 
 * Unified job model for background compute tasks (Optimization, WFA, etc.)
 * Jobs persist across navigation and can be monitored via sidebar + console.
 */

/**
 * Job types supported by the system
 */
export type JobType = 'OPTIMIZATION_RUN' | 'WFA_RUN' | 'PACK_WFA_RUN';

/**
 * Job status following the state machine:
 * QUEUED → INITIALIZING → RUNNING → COMPLETED
 * RUNNING → CANCEL_REQUESTED → CANCELLED
 * RUNNING → FAILED
 * RUNNING → ABORTED
 */
export type JobStatus =
    | 'QUEUED'
    | 'INITIALIZING'
    | 'RUNNING'
    | 'CANCEL_REQUESTED'
    | 'CANCELLED'
    | 'COMPLETED'
    | 'FAILED'
    | 'ABORTED';

/**
 * Log severity levels for console output
 */
export type LogLevel = 'info' | 'warn' | 'error' | 'success' | 'debug';

/**
 * Job context - symbol/timeframe/version info for display
 */
export interface JobContext {
    symbol: string;
    timeframe: string;
    versionLabel?: string;
    strategyName?: string;
}

/**
 * Entity reference - what artifact the job is producing
 */
export interface JobEntityRef {
    kind: 'OPTIMIZATION_VERSION' | 'WFA_REPORT' | 'PACK_WFA_REPORT';
    id: string;
}

/**
 * Job progress snapshot
 */
export interface JobProgress {
    current: number;
    total: number;
    percent: number;
    phase: string;
    message?: string;
}

/**
 * Job timing information
 */
export interface JobTiming {
    createdAt: string;
    queuedAt?: string;
    startedAt?: string;
    heartbeatAt?: string;
    endedAt?: string;
}

/**
 * Job output artifacts
 */
export interface JobOutput {
    artifactRefs: JobEntityRef[];
    partial: boolean;
}

/**
 * Job attempt/retry information
 */
export interface JobAttempts {
    count: number;
    max: number;
    lastError?: {
        code: string;
        message: string;
        stack?: string;
    };
}

/**
 * Job control state (cancellation)
 */
export interface JobControl {
    cancelRequestedAt?: string;
    abortRequestedAt?: string;
}

/**
 * Core Job entity
 */
export interface Job {
    id: string;
    type: JobType;
    status: JobStatus;
    priority?: number;
    
    // Ownership
    projectId?: string;
    userId?: string;
    
    // What artifact is being produced
    entityRef?: JobEntityRef;
    
    // Display context
    context: JobContext;
    
    // Job-specific settings (varies by type)
    settings?: Record<string, unknown>;
    
    // Current progress
    progress: JobProgress;
    
    // Timing
    timing: JobTiming;
    
    // Retry info
    attempts?: JobAttempts;
    
    // Control state
    control?: JobControl;
    
    // Output
    output?: JobOutput;
}

/**
 * Log line entry for console output
 */
export interface JobLogLine {
    timestamp: string;
    level: LogLevel;
    message: string;
    meta?: Record<string, unknown>;
}

/**
 * Log chunk for batched log retrieval
 */
export interface JobLogChunk {
    jobId: string;
    seq: number;
    createdAt: string;
    lines: JobLogLine[];
}

/**
 * Console attachment state
 */
export interface ConsoleAttachment {
    jobId: string | null;
    autoScroll: boolean;
    logs: JobLogLine[];
    lastSeq: number;
}

/**
 * Helper functions
 */

/**
 * Check if a job is in an active state
 */
export function isJobActive(status: JobStatus): boolean {
    return ['QUEUED', 'INITIALIZING', 'RUNNING', 'CANCEL_REQUESTED'].includes(status);
}

/**
 * Check if a job is in a terminal state
 */
export function isJobTerminal(status: JobStatus): boolean {
    return ['COMPLETED', 'FAILED', 'CANCELLED', 'ABORTED'].includes(status);
}

/**
 * Get display label for job type
 */
export function getJobTypeLabel(type: JobType): string {
    switch (type) {
        case 'OPTIMIZATION_RUN':
            return 'Optimization';
        case 'WFA_RUN':
            return 'Walk-Forward Analysis';
        case 'PACK_WFA_RUN':
            return 'Multi-Asset WFA';
        default:
            return 'Unknown Job';
    }
}

/**
 * Get status color class for styling
 * Note: Completed jobs use neutral styling to avoid "marketing success state" feel
 */
export function getJobStatusColor(status: JobStatus): {
    text: string;
    bg: string;
    border: string;
} {
    switch (status) {
        case 'RUNNING':
            return {
                text: 'text-blue-600 dark:text-blue-400',
                bg: 'bg-blue-100 dark:bg-blue-900/30',
                border: 'border-blue-200 dark:border-blue-700',
            };
        case 'QUEUED':
        case 'INITIALIZING':
            return {
                text: 'text-gray-500 dark:text-gray-400',
                bg: 'bg-gray-100 dark:bg-gray-800',
                border: 'border-gray-200 dark:border-gray-700',
            };
        case 'COMPLETED':
            // Neutral styling - success is implied, not shouted
            return {
                text: 'text-gray-600 dark:text-gray-300',
                bg: 'bg-gray-50 dark:bg-gray-800',
                border: 'border-gray-200 dark:border-gray-700',
            };
        case 'FAILED':
        case 'ABORTED':
            return {
                text: 'text-red-500 dark:text-red-400',
                bg: 'bg-red-50 dark:bg-red-900/20',
                border: 'border-red-200 dark:border-red-800',
            };
        case 'CANCELLED':
        case 'CANCEL_REQUESTED':
            return {
                text: 'text-gray-500 dark:text-gray-400',
                bg: 'bg-gray-100 dark:bg-gray-700',
                border: 'border-gray-200 dark:border-gray-600',
            };
        default:
            return {
                text: 'text-gray-600 dark:text-gray-400',
                bg: 'bg-gray-100 dark:bg-gray-700',
                border: 'border-gray-200 dark:border-gray-600',
            };
    }
}

/**
 * Valid state transitions for the job state machine
 */
export const VALID_TRANSITIONS: Record<JobStatus, JobStatus[]> = {
    QUEUED: ['INITIALIZING', 'ABORTED'],
    INITIALIZING: ['RUNNING', 'FAILED', 'ABORTED'],
    RUNNING: ['COMPLETED', 'FAILED', 'CANCEL_REQUESTED', 'ABORTED'],
    CANCEL_REQUESTED: ['CANCELLED', 'COMPLETED', 'FAILED'],
    CANCELLED: [],
    COMPLETED: [],
    FAILED: ['QUEUED'], // Can retry
    ABORTED: [],
};

/**
 * Check if a state transition is valid
 */
export function isValidTransition(from: JobStatus, to: JobStatus): boolean {
    return VALID_TRANSITIONS[from]?.includes(to) ?? false;
}
